import { useState, useRef, useEffect } from 'react'
import { MdPlayArrow, MdPause } from 'react-icons/md'

interface AudioPlayerProps {
    src: string
}

export default function AudioPlayer({ src }: AudioPlayerProps) {
    const [playing, setPlaying] = useState(false)
    const [progress, setProgress] = useState(0)
    const [duration, setDuration] = useState(0)
    const audioRef = useRef<HTMLAudioElement>(null)

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const handleLoadedMetadata = () => {
            setDuration(audio.duration)
        }

        const handleTimeUpdate = () => {
            setProgress(audio.currentTime)
        }

        const handleEnded = () => {
            setPlaying(false)
            setProgress(0)
        }

        audio.addEventListener('loadedmetadata', handleLoadedMetadata)
        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('ended', handleEnded)

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
            audio.removeEventListener('timeupdate', handleTimeUpdate)
            audio.removeEventListener('ended', handleEnded)
        }
    }, [])

    const togglePlay = () => {
        const audio = audioRef.current
        if (!audio) return

        if (playing) {
            audio.pause()
        } else {
            audio.play()
        }
        setPlaying(!playing)
    }

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current
        if (!audio) return

        const value = parseFloat(e.target.value)
        audio.currentTime = value
        setProgress(value)
    }

    const formatTime = (time: number) => {
        if (!isFinite(time) || isNaN(time)) return '0:00'
        const minutes = Math.floor(time / 60)
        const seconds = Math.floor(time % 60)
        return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }

    return (
        <div className="flex items-center gap-2 p-2 bg-base-200 rounded-lg max-w-xs">
            <button
                onClick={togglePlay}
                className="btn btn-circle btn-sm btn-secondary"
            >
                {playing ? (
                    <MdPause className="text-lg" />
                ) : (
                    <MdPlayArrow className="text-lg" />
                )}
            </button>
            <div className="flex-1">
                <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    value={progress}
                    onChange={handleSeek}
                    className="range range-xs range-secondary w-full"
                    step="0.1"
                />
                <div className="text-xs text-base-content/70 mt-0.5">
                    {formatTime(progress)} / {formatTime(duration)}
                </div>
            </div>
            <audio ref={audioRef} src={src} preload="metadata" />
        </div>
    )
}
